/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Link, useParams } from "react-router-dom";
import { Channel, Emoji as CustomEmoji, User } from "revolt.js";
import styled, { css } from "styled-components/macro";
import { RefObject } from "preact";
import { StateUpdater, useRef, useState } from "preact/hooks";

import { defer } from "../../lib/defer";
import { getDisplayName } from "../../lib/displayName";
import { getRolesInChannel } from "../../lib/getRolesInChannel";

import { emojiDictionary } from "../../assets/emojis";
import { useClient } from "../../controllers/client/ClientController";
import ChannelIcon from "./ChannelIcon";
import Emoji from "./Emoji";
import ServerIcon from "./ServerIcon";
import Tooltip from "./Tooltip";
import UserIcon from "./user/UserIcon";

type Role = {
    type: "role";
    id: string;
    name: string;
};

type Everyone = {
    type: "everyone";
};

export type AutoCompleteState =
    | { type: "none" }
    | ({ selected: number; within: boolean } & (
          | {
                type: "emoji";
                matches: (string | CustomEmoji)[];
            }
          | {
                type: "user";
                matches: (User | Role | Everyone)[];
            }
          | {
                type: "channel";
                matches: Channel[];
            }
      ));

export type SearchClues = {
    users?: { type: "channel"; id: string } | { type: "all" };
    channels?: { server: string };
};

export type AutoCompleteProps = {
    detached?: boolean;
    state: AutoCompleteState;
    setState: StateUpdater<AutoCompleteState>;
    includeRoles: boolean;

    onKeyUp: (ev: KeyboardEvent) => void;
    onKeyDown: (ev: KeyboardEvent) => boolean;
    onChange: (ev: JSX.TargetedEvent<HTMLTextAreaElement, Event>) => void;
    onClick: JSX.MouseEventHandler<HTMLButtonElement>;
    onFocus: JSX.FocusEventHandler<HTMLTextAreaElement>;
    onBlur: JSX.FocusEventHandler<HTMLTextAreaElement>;

    listContainerRef?: RefObject<HTMLDivElement>;
};

export function useAutoComplete(
    setValue: (v?: string) => void,
    includeRoles: boolean,
    searchClues?: SearchClues,
    textAreaId?: string,
): AutoCompleteProps {
    const [state, setState] = useState<AutoCompleteState>({ type: "none" });
    const [focused, setFocused] = useState(false);
    const { server } = useParams<{ server?: string }>();
    const client = useClient();

    function findSearchString( //Improvement #93449 не будет возвращать emoji
        el: HTMLTextAreaElement,
    ): ["emoji" | "user" | "channel", string, number] | undefined {
        if (el.selectionStart === el.selectionEnd) {
            const cursor = el.selectionStart;
            const content = el.value.slice(0, cursor);

            const valid = /[\w\-АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ]/i;

            let j = content.length - 1;
            if (content[j] === "@") {
                return ["user", "", j];
            } else if (content[j] === "#") {
                return ["channel", "", j];
            }

            while (j >= 0 && valid.test(content[j])) {
                j--;
            }

            if (j === -1) return;
            const current = content[j];

            //Improvement #93449
            if (current === "@" || current === "#") {
                //current === ":" ||
                const search = content.slice(j + 1, content.length);
                const minLen = 1; //current === ":" ? 2 :

                if (search.length >= minLen) {
                    return [
                        current === "#"
                            ? "channel"
                            : // : current === ":"
                              // ? "emoji"
                              "user",
                        search.toLowerCase(),
                        j, //current === ":" ? j + 1 :
                    ];
                }
            }
        }
    }

    function onChange(ev: JSX.TargetedEvent<HTMLTextAreaElement, Event>) {
        const el = ev.currentTarget;
        const result = findSearchString(el);
        if (result) {
            const [type, search] = result;
            const regex = new RegExp(search, "i");

            if (type === "emoji") {
                // ! TODO: we should convert it to a Binary Search Tree and use that
                const matches = [
                    ...Object.keys(emojiDictionary).filter((emoji: string) =>
                        emoji.match(regex),
                    ),
                    ...Array.from(client.emojis.values()).filter((emoji) =>
                        emoji.name.match(regex),
                    ),
                ].splice(0, 5);

                if (matches.length > 0) {
                    const currentPosition =
                        state.type !== "none" ? state.selected : 0;

                    setState({
                        // @ts-ignore-next-line are you high
                        type: "emoji",
                        // @ts-ignore-next-line
                        matches,
                        selected: Math.min(currentPosition, matches.length - 1),
                        within: false,
                    });

                    return;
                }
            }

            if (type === "user" && searchClues?.users) {
                let users: User[] = [];
                switch (searchClues.users.type) {
                    case "all":
                        users = [...client.users.values()];
                        break;
                    case "channel": {
                        const channel = client.channels.get(
                            searchClues.users.id,
                        );
                        switch (channel?.channel_type) {
                            case "Group":
                                users = channel.recipients!.filter(
                                    (x) => typeof x !== "undefined",
                                ) as User[];
                                break;
                            case "TextChannel":
                                {
                                    const server_id = channel.server_id;
                                    users = [...client.members.keys()]
                                        .map((x) => JSON.parse(x))
                                        .filter((x) => x.server === server_id)
                                        .filter((x) => {
                                            if (server) {
                                                const member =
                                                    client.members.getKey({
                                                        server,
                                                        user: x.user,
                                                    });
                                                return member?.hasPermission(
                                                    channel,
                                                    "ViewChannel",
                                                );
                                            }
                                        })
                                        .map((x) => client.users.get(x.user))
                                        .map((x) => {
                                            if (x && server) {
                                                const member =
                                                    client.members.getKey({
                                                        server,
                                                        user: x._id,
                                                    });

                                                if (member?.nickname) {
                                                    return {
                                                        ...x,
                                                        nickname:
                                                            member.nickname,
                                                    };
                                                }
                                            }
                                            return x;
                                        })
                                        .filter(
                                            (x) => typeof x !== "undefined",
                                        ) as User[];
                                }
                                break;
                            default:
                                return;
                        }
                    }
                }

                users = users.filter(
                    (x) =>
                        x._id !== "00000000000000000000000000" &&
                        !!x.ldap_info &&
                        !x.ldap_info?.retired,
                );

                const matches = (
                    search.length > 0
                        ? users.filter(
                              (user) =>
                                  user.username.toLowerCase().match(regex) ||
                                  user.display_name
                                      ?.toLowerCase()
                                      .match(regex) ||
                                  user.nickname?.toLowerCase().match(regex),
                          )
                        : users
                )
                    .splice(0, 25)
                    .filter((x) => typeof x !== "undefined");

                let roles: Role[] = [];
                const everyone: Everyone[] = [];

                if (searchClues.users.type === "channel") {
                    const channel = client.channels.get(searchClues.users.id);

                    if (!channel) return;
                    const channelRoles = getRolesInChannel(channel);

                    channelRoles.forEach((role) => {
                        if (role.name.toLowerCase().match(regex)) {
                            roles.push({
                                id: role.id,
                                name: role.name,
                                type: "role",
                            });
                        }
                    });

                    roles = roles.slice(0, 25);
                }

                if ("everyone".includes(search) || "все".includes(search)) {
                    everyone.push({ type: "everyone" });
                }

                const result = [...matches, ...roles, ...everyone];

                if (result.length > 0) {
                    const currentPosition =
                        state.type !== "none" ? state.selected : 0;

                    setState({
                        type: "user",
                        matches: result,
                        selected: Math.min(currentPosition, result.length - 1),
                        within: false,
                    });

                    return;
                }
            }

            if (type === "channel" && searchClues?.channels) {
                const channels = client.servers
                    .get(searchClues.channels.server)
                    ?.channels.filter(
                        (x) => typeof x !== "undefined",
                    ) as Channel[];

                const matches = (
                    search.length > 0
                        ? channels.filter((channel) =>
                              channel.name!.toLowerCase().match(regex),
                          )
                        : channels
                )
                    .splice(0, 5)
                    .filter((x) => typeof x !== "undefined");

                if (matches.length > 0) {
                    const currentPosition =
                        state.type !== "none" ? state.selected : 0;

                    setState({
                        type: "channel",
                        matches,
                        selected: Math.min(currentPosition, matches.length - 1),
                        within: false,
                    });

                    return;
                }
            }
        }

        if (state.type !== "none") {
            setState({ type: "none" });
        }
    }

    const selectionCurrentRef = useRef<NodeJS.Timeout | null>(null);

    function selectCurrent(el: HTMLTextAreaElement) {
        if (state.type !== "none") {
            const result = findSearchString(el);
            if (result) {
                const [_type, search, index] = result;

                const content = el.value.split("");
                if (state.type === "emoji") {
                    const selected = state.matches[state.selected];
                    content.splice(
                        index,
                        search.length,
                        selected instanceof CustomEmoji
                            ? selected._id
                            : selected,
                        ": ",
                    );
                } else if (state.type === "user") {
                    const selected = state.matches[state.selected];
                    if ("type" in selected) {
                        if (selected.type === "everyone") {
                            const text = "everyone";

                            content.splice(
                                index,
                                text.length + 1,
                                "<@",
                                text,
                                "> ",
                            );
                        }
                        if (selected.type === "role") {
                            content.splice(
                                index,
                                search.length + 1,
                                "<@",
                                selected.id,
                                "> ",
                            );
                        }
                    } else {
                        content.splice(
                            index,
                            search.length + 1,
                            "<@",
                            selected._id,
                            "> ",
                        );
                    }
                } else {
                    content.splice(
                        index,
                        search.length + 1,
                        "<#",
                        state.matches[state.selected]._id,
                        "> ",
                    );
                }

                const contentString = content.join("");
                const cursorPositionBefore = el.selectionStart;

                clearTimeout(selectionCurrentRef.current!);
                selectionCurrentRef.current = defer(() => {
                    selectionCurrentRef.current = null;
                    const txtValue = el.value;
                    for (
                        let i = cursorPositionBefore;
                        i < txtValue.length;
                        i++
                    ) {
                        const char = txtValue.charAt(i);
                        if (char === ">") {
                            // Было const cursorPosition = i + 1;
                            // https://redmine.o-code.ru/issues/76943
                            const cursorPosition = i + 2;
                            el.selectionStart = cursorPosition;
                            el.selectionEnd = cursorPosition;
                            return;
                        }
                    }
                });
                setValue(contentString);
            }
        }
    }

    function onClick(ev: JSX.TargetedMouseEvent<HTMLButtonElement>) {
        if (!textAreaId) return;
        ev.preventDefault();
        const textAreaElement = document.querySelector(
            `#${textAreaId}`,
        ) as HTMLTextAreaElement;
        selectCurrent(textAreaElement);
        setFocused(false);
    }

    const listContainerRef = useRef<HTMLDivElement>(null);

    const scrollToSelectedItem = () => {
        if (listContainerRef.current && state.type !== "none") {
            const selectedElement = listContainerRef.current.querySelector(
                `button:nth-child(${state.selected + 1})`,
            );

            if (selectedElement) {
                selectedElement.scrollIntoView({
                    block: "center",
                });
            }
        }
    };

    function onKeyDown(e: KeyboardEvent) {
        if (focused && state.type !== "none") {
            if (e.key === "ArrowUp") {
                e.preventDefault();
                if (state.selected > 0) {
                    setState({
                        ...state,
                        selected: state.selected - 1,
                    });

                    scrollToSelectedItem();
                }

                return true;
            }

            if (e.key === "ArrowDown") {
                e.preventDefault();
                if (state.selected < state.matches.length - 1) {
                    setState({
                        ...state,
                        selected: state.selected + 1,
                    });

                    scrollToSelectedItem();
                }

                return true;
            }

            if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                selectCurrent(e.currentTarget as HTMLTextAreaElement);

                return true;
            }
        }

        return false;
    }

    /*************  ✨ Codeium Command ⭐  *************/
    /**
     * Handles the keyup event for the autocomplete input.
     * Triggers the onChange function if the current target is not null.
     *
     * @param e - The keyboard event triggered by the user.
     */

    /******  17dc5ae0-0430-49cd-a571-df4f5108ef8c  *******/ function onKeyUp(
        e: KeyboardEvent,
    ) {
        if (e.currentTarget !== null) {
            // @ts-expect-error Type mis-match.
            onChange(e);
        }
    }

    function onFocus(ev: JSX.TargetedFocusEvent<HTMLTextAreaElement>) {
        setFocused(true);
        onChange(ev);
    }

    function onBlur() {
        if (state.type !== "none" && state.within) return;
        setFocused(false);
    }

    return {
        state: focused ? state : { type: "none" },
        setState,
        includeRoles,

        onClick,
        onChange,
        onKeyUp,
        onKeyDown,
        onFocus,
        onBlur,

        listContainerRef,
    };
}

const Base = styled.div<{ detached?: boolean }>`
    position: relative;

    > div {
        bottom: 0;
        width: 100%;
        position: absolute;
        background: var(--primary-header);
        max-height: 300px;
        overflow-y: auto;
    }

    button {
        gap: 8px;
        margin: 4px;
        padding: 6px;
        border: none;
        display: flex;
        font-size: 1em;
        cursor: pointer;
        align-items: center;
        flex-direction: row;
        font-family: inherit;
        background: transparent;
        color: var(--foreground);
        width: calc(100% - 12px);
        border-radius: var(--border-radius);

        span {
            display: grid;
            place-items: center;
        }

        &.active {
            background: var(--primary-background);
        }
    }

    ${(props) =>
        props.detached &&
        css`
            bottom: 8px;

            > div {
                border-radius: var(--border-radius);
            }
        `}
`;

export default function AutoComplete({
    detached,
    state,
    setState,
    onClick,
    listContainerRef,
}: Pick<
    AutoCompleteProps,
    "detached" | "state" | "setState" | "onClick" | "listContainerRef"
>) {
    const client = useClient();
    return (
        <Base detached={detached}>
            <div ref={listContainerRef}>
                {state.type === "emoji" &&
                    state.matches.map((match, i) => (
                        <button
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                            key={match}
                            className={i === state.selected ? "active" : ""}
                            onMouseEnter={() =>
                                (i !== state.selected || !state.within) &&
                                setState({
                                    ...state,
                                    selected: i,
                                    within: true,
                                })
                            }
                            onMouseLeave={() =>
                                state.within &&
                                setState({
                                    ...state,
                                    within: false,
                                })
                            }
                            onClick={onClick}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "center",
                                }}>
                                {match instanceof CustomEmoji ? (
                                    <img
                                        loading="lazy"
                                        src={match.imageURL}
                                        style={{
                                            width: `20px`,
                                            height: `20px`,
                                        }}
                                    />
                                ) : (
                                    <Emoji
                                        emoji={
                                            (
                                                emojiDictionary as Record<
                                                    string,
                                                    string
                                                >
                                            )[match]
                                        }
                                        size={20}
                                    />
                                )}
                                <span style={{ paddingLeft: "4px" }}>{`:${
                                    match instanceof CustomEmoji
                                        ? match.name
                                        : match
                                }:`}</span>
                            </div>
                            {match instanceof CustomEmoji &&
                                match.parent.type == "Server" && (
                                    <>
                                        <Tooltip
                                            content={
                                                client.servers.get(
                                                    match.parent.id,
                                                )?.name
                                            }>
                                            <Link
                                                to={`/server/${match.parent.id}`}>
                                                <ServerIcon
                                                    target={client.servers.get(
                                                        match.parent.id,
                                                    )}
                                                    size={20}
                                                />
                                            </Link>
                                        </Tooltip>
                                    </>
                                )}
                        </button>
                    ))}
                {state.type === "user" &&
                    state.matches.map((match, i) => (
                        <button
                            key={match}
                            className={i === state.selected ? "active" : ""}
                            onMouseEnter={() =>
                                (i !== state.selected || !state.within) &&
                                setState({
                                    ...state,
                                    selected: i,
                                    within: true,
                                })
                            }
                            onMouseLeave={() =>
                                state.within &&
                                setState({
                                    ...state,
                                    within: false,
                                })
                            }
                            onClick={onClick}>
                            {"type" in match ? (
                                match.type === "everyone" ? (
                                    "@everyone"
                                ) : (
                                    `@${match.name}`
                                )
                            ) : (
                                <>
                                    <UserIcon
                                        size={24}
                                        target={match}
                                        status={true}
                                    />
                                    {getDisplayName(match, match.nickname)}
                                </>
                            )}
                        </button>
                    ))}
                {state.type === "channel" &&
                    state.matches.map((match, i) => (
                        <button
                            key={match}
                            className={i === state.selected ? "active" : ""}
                            onMouseEnter={() =>
                                (i !== state.selected || !state.within) &&
                                setState({
                                    ...state,
                                    selected: i,
                                    within: true,
                                })
                            }
                            onMouseLeave={() =>
                                state.within &&
                                setState({
                                    ...state,
                                    within: false,
                                })
                            }
                            onClick={onClick}>
                            <ChannelIcon size={24} target={match} />
                            {match.name}
                        </button>
                    ))}
            </div>
        </Base>
    );
}
